import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Share2, FileText, ShieldCheck, Check } from 'lucide-react';
import OFFERS_DETAIL from '../data/offersDetail';
import ContractSignModal from '../components/ContractSignModal';
import { SabrilDecorFrame, BrandMark, SunOrnament } from '../theme/sabril-da';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ContractPage — dedicated shareable page for /contrat/:offer.
 * Shows a recap of the offer + a summary of the 12 legal clauses,
 * then a big CTA that opens ContractSignModal.
 * Only supports economie & affaires (première = sur-mesure).
 */

const CLAUSES_ECONOMIE = [
  { n: '1',  t: 'Parties',                          d: 'Identification prestataire (Audit Resto by Sabril) et client (raison sociale, SIRET/ICE, adresse).' },
  { n: '2',  t: 'Objet',                            d: 'Audit express 48h sur site + méthode complète du restaurant + livrables sur-mesure.' },
  { n: '3',  t: 'Prestations incluses',             d: '48h d\'immersion, PDF stratégique, guide menu, fiches de poste, grille food cost, DA cadrée, suivi 30j WhatsApp.' },
  { n: '4',  t: 'Durée',                            d: 'J-7 préparation · J+0 48h sur place · J+7 livraison · 30 jours de suivi WhatsApp.' },
  { n: '5',  t: 'Tarif & modalités',                d: '2 790€ TTC (FR/ES) ou 2 190€ TTC (MA), payé par Stripe. 100% déductible d\'une offre supérieure. Logement + transport du prestataire réservés directement par le client (non compris dans le tarif).' },
  { n: '6',  t: 'Obligations prestataire',          d: 'Respect des délais, confidentialité, livraison des documents dans les 7 jours suivant l\'immersion.' },
  { n: '7',  t: 'Obligations client',               d: 'Accès total au restaurant, transmission P&L / carte / photos, disponibilité pendant les 48h. Réservation directe du logement (hôtel 3★+) et du transport du prestataire (aller/retour depuis Marrakech + trajets locaux).' },
  { n: '8',  t: 'Confidentialité',                  d: 'NDA mutuel · aucune information partagée avec un tiers sans accord écrit.' },
  { n: '9',  t: 'Protection des données (RGPD)',    d: 'Traitement RGPD · droit d\'accès, rectification et suppression sur simple demande email.' },
  { n: '10', t: 'Résiliation & annulation',         d: 'Annulation possible jusqu\'à J-7 avec remboursement 100%. Après : remboursement 50%.' },
  { n: '11', t: 'Droit applicable',                 d: 'Droit français. Litiges soumis aux tribunaux compétents du siège du prestataire.' },
  { n: '12', t: 'Signature électronique',           d: 'Valeur légale équivalente à une signature manuscrite (Art. 1367 du Code civil).' },
];

const CLAUSES_AFFAIRES = [
  { n: '1',  t: 'Parties',                          d: 'Identification prestataire (Audit Resto by Sabril) et client (raison sociale, SIRET/ICE, adresse).' },
  { n: '2',  t: 'Objet',                            d: 'Mission de pilotage stratégique à distance sur 90 jours — Sabril comme bras droit du dirigeant.' },
  { n: '3',  t: 'Prestations incluses',             d: 'WhatsApp prioritaire <24h, 2 visios/mois, coaching hebdo 1h, plan d\'action mensuel, tableau de bord KPI.' },
  { n: '4',  t: 'Durée',                            d: '90 jours consécutifs à compter de la date de signature du devis annexé.' },
  { n: '5',  t: 'Tarif & modalités',                d: 'Le tarif définitif fera l\'objet d\'un devis annexé transmis sous 24h. Paiement en 1× ou 3× sans frais.' },
  { n: '6',  t: 'Obligations prestataire',          d: 'Disponibilité, réactivité <24h, respect des rendez-vous planifiés, livrables mensuels.' },
  { n: '7',  t: 'Obligations client',               d: 'Transparence sur les chiffres, exécution des actions convenues, présence aux rendez-vous.' },
  { n: '8',  t: 'Confidentialité',                  d: 'NDA mutuel · aucune information partagée avec un tiers sans accord écrit.' },
  { n: '9',  t: 'Protection des données (RGPD)',    d: 'Traitement RGPD · droit d\'accès, rectification et suppression sur simple demande email.' },
  { n: '10', t: 'Résiliation & annulation',         d: 'Résiliation possible à J+30 avec préavis 15 jours (solde des prestations dues).' },
  { n: '11', t: 'Propriété intellectuelle',         d: 'Les documents livrés restent la propriété du client, avec licence d\'usage interne.' },
  { n: '12', t: 'Droit applicable',                 d: 'Droit français. Litiges soumis aux tribunaux compétents du siège du prestataire.' },
  { n: '13', t: 'Signature électronique',           d: 'Valeur légale équivalente à une signature manuscrite (Art. 1367 du Code civil).' },
];

const CLAUSES_CONFIGURATEUR = [
  { n: '1',  t: 'Parties',                          d: 'Identification prestataire (Audit Resto by Sabril) et client (raison sociale, SIRET/ICE, adresse).' },
  { n: '2',  t: 'Objet & format',                   d: 'Mission d\'accompagnement sur-mesure composée via le configurateur — format + chantiers activés au moment de la signature.' },
  { n: '3',  t: 'Périmètre : chantiers activés',    d: 'Liste explicite des chantiers cochés + tarification unitaire (1 290 € HT) ou pack complet 360° (12 900 € HT).' },
  { n: '4',  t: 'Livrables',                        d: 'Chaque chantier donne lieu à des livrables opérationnels dédiés (documents, SOP, dashboards, calendrier éditorial...).' },
  { n: '5',  t: 'Tarif & modalités',                d: 'Total HT figé au moment de la signature. Paiement virement ou Stripe (1× ou 3× sans frais). Devis nominatif annexé sous 24h.' },
  { n: '6',  t: 'Obligations prestataire',          d: 'Bonne exécution, confidentialité, rendez-vous respectés, réponse WhatsApp <24h ouvrées (formats distanciels).' },
  { n: '7',  t: 'Obligations client',               d: 'Transparence sur les chiffres, exécution des actions. Pour formats sur site hors Maroc : logement + transport à la charge du client.' },
  { n: '8',  t: 'Confidentialité',                  d: 'NDA mutuel · aucune information partagée avec un tiers sans accord écrit.' },
  { n: '9',  t: 'Protection des données (RGPD)',    d: 'Traitement RGPD · droit d\'accès, rectification et suppression sur simple demande email.' },
  { n: '10', t: 'Résiliation & annulation',         d: 'Préavis 30j pour distanciel · 15j avant kickoff pour formats sur site. Prestations réalisées demeurent acquises.' },
  { n: '11', t: 'Propriété intellectuelle',         d: 'Livrables restent la propriété du prestataire. Client bénéficie d\'un droit d\'usage privé.' },
  { n: '12', t: 'Droit applicable',                 d: 'Droit français. Résolution amiable puis tribunaux du ressort du siège du prestataire.' },
  { n: '13', t: 'Signature électronique',           d: 'Valeur légale équivalente à une signature manuscrite (Art. 1367 du Code civil).' },
];

const CONFIGURATEUR_PLACEHOLDER = {
  key: 'configurateur',
  title: 'Configurateur sur-mesure.',
  tagline: 'Composé via le configurateur Sabril Audit Resto.',
  price: { main: 'Sur devis', sub: 'total calculé selon vos chantiers' },
  sections: [
    {
      title: 'Ce qui est inclus',
      items: [
        { title: 'Format choisi (distanciel · 48h · terrain 17j · hybride)' },
        { title: 'Chantiers activés à 1 290 € HT / chantier' },
        { title: 'Livrables opérationnels par chantier' },
        { title: 'Devis nominatif annexé sous 24h' },
      ],
    },
  ],
};

export default function ContractPage() {
  const { offer: offerKey } = useParams();
  const navigate = useNavigate();
  const isEconomie = offerKey === 'economie';
  const isAffaires = offerKey === 'affaires';
  const isConfigurateur = offerKey === 'configurateur';
  const [previewHtml, setPreviewHtml] = useState('');
  const [configPrefill, setConfigPrefill] = useState(null);

  // Load configurateur prefill from sessionStorage (set by ConfiguratorPage)
  useEffect(() => {
    if (!isConfigurateur) return;
    try {
      const raw = sessionStorage.getItem('sabril_config_prefill');
      if (raw) setConfigPrefill(JSON.parse(raw));
    } catch (_e) { /* ignore */ }
  }, [isConfigurateur]);

  const offer = isConfigurateur ? CONFIGURATEUR_PLACEHOLDER : OFFERS_DETAIL[offerKey];
  const clauses = isEconomie ? CLAUSES_ECONOMIE : isConfigurateur ? CLAUSES_CONFIGURATEUR : CLAUSES_AFFAIRES;

  useEffect(() => {
    if (!offer) return;
    document.title = `Signer mon contrat · ${offer.title.replace(/\.$/, '')}`;
    window.scrollTo(0, 0);
  }, [offer]);

  useEffect(() => {
    if (!isEconomie && !isAffaires && !isConfigurateur) return;
    let alive = true;
    let url = `${API_URL}/api/contract/preview?offer=${offerKey}`;
    if (isConfigurateur && configPrefill) {
      const params = new URLSearchParams({
        offer: 'configurateur',
        config_format: configPrefill.format || 'distanciel',
        config_country: configPrefill.country || '',
        config_chantiers: (configPrefill.chantiers || []).join(','),
        config_full_pack: String(!!configPrefill.full_pack),
      });
      if (configPrefill.total_ht != null) params.set('config_total_ht', String(configPrefill.total_ht));
      if (configPrefill.contact?.firstName) params.set('firstName', configPrefill.contact.firstName);
      if (configPrefill.contact?.restaurant) params.set('restaurant_name', configPrefill.contact.restaurant);
      if (configPrefill.contact?.email) params.set('email', configPrefill.contact.email);
      if (configPrefill.contact?.phone) params.set('phone', configPrefill.contact.phone);
      url = `${API_URL}/api/contract/preview?${params.toString()}`;
    }
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        if (!alive) return;
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        setPreviewHtml(bodyMatch ? bodyMatch[1] : html);
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [offerKey, isEconomie, isAffaires, isConfigurateur, configPrefill]);

  if (!offer || (!isEconomie && !isAffaires && !isConfigurateur)) {
    return (
      <div className="min-h-screen bg-[#f5f3f0] flex flex-col items-center justify-center text-center px-6" data-testid="contract-page-404">
        <h1 className="text-2xl font-black mb-3">Contrat introuvable</h1>
        <p className="text-gray-500 mb-6">Ce contrat n&apos;est disponible que pour Classe Économie, Classe Affaires et le Configurateur.</p>
        <Link to="/prsnl" className="px-4 py-2.5 bg-black text-white rounded-full font-bold text-sm">
          ← Voir toutes les offres
        </Link>
      </div>
    );
  }

  const openModal = () => {
    window.dispatchEvent(new CustomEvent('contract:open', {
      detail: { offer: offerKey, prefill: configPrefill },
    }));
  };

  const share = async () => {
    const shareData = {
      title: `Contrat · ${offer.title}`,
      text: `Signer mon contrat : ${offer.title}`,
      url: window.location.href,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (_e) { /* cancelled */ }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier');
    }
  };

  const accent = isEconomie ? '#0F7A78' : isConfigurateur ? '#D89440' : '#8B5CF6';
  const accentDark = isEconomie ? '#076665' : isConfigurateur ? '#B8792F' : '#6D28D9';
  const heroBg = isEconomie
    ? 'radial-gradient(120% 80% at 50% 0%, #0F7A78 0%, #06413F 60%, #031F1D 100%)'
    : isConfigurateur
      ? 'radial-gradient(120% 80% at 50% 0%, #1A4438 0%, #0B2B24 60%, #041613 100%)'
      : 'radial-gradient(120% 80% at 50% 0%, #8B5CF6 0%, #4B2A9E 60%, #1F1247 100%)';

  // Configurator prefill decoding
  const cfgFormatLabel = configPrefill?.format_label || (
    configPrefill?.format === 'distanciel' ? '100% à distance · 90 jours' :
    configPrefill?.format === 'audit48' ? 'Audit intensif 48h sur site' :
    configPrefill?.format === 'terrain17' ? 'Accompagnement 17 jours sur site' :
    configPrefill?.format === 'hybride' ? '90 jours + 15 jours terrain' : ''
  );
  const cfgTotal = configPrefill?.total_ht != null
    ? `${new Intl.NumberFormat('fr-FR').format(configPrefill.total_ht)} € HT`
    : 'Sur devis';
  const cfgChantierCount = configPrefill?.full_pack
    ? '15 chantiers · Pack complet 360°'
    : `${(configPrefill?.chantiers || []).length} chantier${(configPrefill?.chantiers || []).length > 1 ? 's' : ''} activé${(configPrefill?.chantiers || []).length > 1 ? 's' : ''}`;

  return (
    <div className="min-h-screen bg-[#F7EEDF] pb-40 relative overflow-x-hidden" data-testid={`contract-page-${offerKey}`}>
      <SabrilDecorFrame awningTL awningBR mimosa palm awningSize={130} mimosaSize={120} />
      <style>{`
        .contract-preview-scope .kicker { text-transform: uppercase; letter-spacing: 3px; font-size: 9px; color: #F05A1A; font-weight: 700; font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif; margin: 0 0 4px; }
        .contract-preview-scope h1 { font-size: 20px; font-weight: 900; margin: 4px 0 14px; line-height: 1.1; color: #0B2A28; font-family: "Playfair Display", Georgia, serif; letter-spacing: -0.015em; }
        .contract-preview-scope h2 { font-size: 13px; font-weight: 900; margin: 16px 0 6px; color: #0F2A47; letter-spacing: -0.01em; font-family: "Playfair Display", Georgia, serif; }
        .contract-preview-scope p { margin: 0 0 6px; text-align: justify; font-size: 11.5px; line-height: 1.55; color: #1a1a1a; }
        .contract-preview-scope ul { padding-left: 18px; margin: 6px 0 8px; }
        .contract-preview-scope li { font-size: 11.5px; line-height: 1.55; margin: 3px 0; color: #1a1a1a; }
        .contract-preview-scope .box { background: #f5f3f0; padding: 10px 12px; border-radius: 8px; margin: 8px 0; border: 0.5px solid rgba(0,0,0,0.06); }
        .contract-preview-scope .box p { margin: 3px 0; font-size: 11px; }
        .contract-preview-scope .label { font-family: -apple-system, BlinkMacSystemFont, sans-serif; font-size: 8.5px; text-transform: uppercase; letter-spacing: 1.5px; color: #666; font-weight: 700; }
        .contract-preview-scope .val { font-weight: 700; color: #1a1a1a; }
        .contract-preview-scope .stamp { display: inline-block; padding: 3px 9px; background: #C9A87A22; border: 1px solid #C9A87A; border-radius: 999px; font-size: 8.5px; letter-spacing: 1.5px; color: #8B6520; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; }
        .contract-preview-scope .signatures { display: flex; gap: 14px; margin-top: 18px; flex-wrap: wrap; }
        .contract-preview-scope .sig { flex: 1; min-width: 120px; border: 1px dashed #ccc; padding: 10px 12px; border-radius: 8px; min-height: 60px; }
        .contract-preview-scope .sig-name { font-family: "Playfair Display", Georgia, serif; font-style: italic; font-size: 14px; color: #0F2A47; margin-top: 4px; }
        .contract-preview-scope .sig-date { font-size: 8.5px; color: #999; margin-top: 6px; }
      `}</style>

      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 px-4 py-3 flex items-center justify-between"
           style={{ background: 'rgba(245,243,240,0.92)', backdropFilter: 'blur(14px)', borderBottom: '0.5px solid rgba(0,0,0,0.06)' }}>
        <button
          type="button"
          onClick={() => navigate(isConfigurateur ? '/configurer' : `/offre/${offerKey}`)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full active:scale-95 transition"
          style={{ background: 'rgba(0,0,0,0.06)', color: '#1a1a1a' }}
          data-testid="contract-page-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span className="text-xs font-bold tracking-wide">{isConfigurateur ? 'Retour configurateur' : 'Retour à l\u2019offre'}</span>
        </button>
        <button
          type="button"
          onClick={share}
          className="p-2 rounded-full active:scale-95 transition"
          style={{ background: 'rgba(0,0,0,0.06)', color: '#1a1a1a' }}
          aria-label="Partager"
          data-testid="contract-page-share"
        >
          <Share2 className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      </div>

      {/* HERO */}
      <div className="relative px-5 pt-8 pb-8 mx-4 mt-4 rounded-[28px] overflow-hidden"
           style={{ background: heroBg, color: '#FFFFFF' }}>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
             style={{ background: 'rgba(255,255,255,0.10)', border: `0.5px solid ${accent}66` }}>
          <ShieldCheck className="w-3 h-3" style={{ color: accent }} strokeWidth={3} />
          <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 9.5, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 800, color: accent }}>
            Signature électronique · Valeur légale
          </span>
        </div>
        <p style={{ fontFamily: '"Space Mono", monospace', fontSize: 10, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', color: accent }}>
          {isEconomie
            ? 'Contrat · Classe Économie 48h'
            : isConfigurateur
              ? `Contrat sur-mesure · ${cfgFormatLabel || 'Configurateur'}`
              : 'Contrat de mission · Classe Affaires 90j'}
        </p>
        <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: 30, fontWeight: 900, letterSpacing: '-0.025em', lineHeight: 1.02, marginTop: 10 }}>
          Signer mon contrat.
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', marginTop: 10, lineHeight: 1.45 }}>
          {isEconomie
            ? 'Contrat de prestation en 12 articles · signé en ligne · reçu par email · paiement Stripe sécurisé juste après.'
            : isConfigurateur
              ? 'Contrat sur-mesure en 13 articles · vos chantiers sont pré-remplis · je te contacte sous 24h avec le devis nominatif annexé.'
              : 'Contrat de mission en 13 articles · signé en ligne · reçu par email · je te contacte sous 24h avec le devis nominatif annexé.'}
        </p>

        {/* Recap card */}
        {isConfigurateur ? (
          <div className="mt-6 rounded-2xl px-4 py-4"
               style={{ background: 'rgba(0,0,0,0.28)', border: `0.5px solid ${accent}55`, backdropFilter: 'blur(8px)' }}
               data-testid="contract-recap-configurateur">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p style={{ fontFamily: '"Space Mono", monospace', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                  Format & périmètre
                </p>
                <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 17, fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.015em', lineHeight: 1.1, marginTop: 4 }}>
                  {cfgFormatLabel || 'Format à choisir'}
                </p>
                <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)', marginTop: 3, fontFamily: '"Space Mono", monospace', letterSpacing: '0.06em' }}>
                  {cfgChantierCount}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p style={{ fontFamily: '"Space Mono", monospace', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                  Total
                </p>
                <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 22, fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: 1, marginTop: 4 }}>
                  {cfgTotal}
                </p>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  {configPrefill?.total_ht != null ? 'HT · figé au contrat' : 'sur devis 24h'}
                </p>
              </div>
            </div>
            {!!(configPrefill?.chantiers?.length || configPrefill?.full_pack) && (
              <div className="mt-3 pt-3 flex flex-wrap gap-1.5" style={{ borderTop: `0.5px solid ${accent}33` }}>
                {(configPrefill.full_pack
                  ? Array.from({ length: 15 }, (_, i) => String(i + 1).padStart(2, '0'))
                  : configPrefill.chantiers
                ).map((n) => (
                  <span key={n} style={{
                    fontFamily: '"Space Mono", monospace', fontSize: 9, fontWeight: 800,
                    letterSpacing: '0.14em', padding: '3px 7px', borderRadius: 5,
                    background: `${accent}22`, color: accent, border: `0.5px solid ${accent}44`,
                  }}>
                    N°{n}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 rounded-2xl px-4 py-4"
               style={{ background: 'rgba(0,0,0,0.28)', border: `0.5px solid ${accent}55`, backdropFilter: 'blur(8px)' }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p style={{ fontFamily: '"Space Mono", monospace', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                  Offre
                </p>
                <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 17, fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.015em', lineHeight: 1.1, marginTop: 4 }}>
                  {offer.title}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p style={{ fontFamily: '"Space Mono", monospace', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                  Investissement
                </p>
                <p style={{ fontFamily: '"Playfair Display", serif', fontSize: 22, fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: 1, marginTop: 4 }}>
                  {offer.price.main}
                </p>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{offer.price.sub}</p>
              </div>
            </div>
          </div>
        )}

        {/* Key includes (compact) */}
        <ul className="mt-4 space-y-1.5">
          {(offer.sections?.[1]?.items || offer.sections?.[0]?.items || []).slice(0, 4).map((it) => (
            <li key={it.title} className="flex items-start gap-2">
              <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: accent }} strokeWidth={3} />
              <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>
                {it.title}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Contract clauses */}
      <div className="px-4 mt-8">
        <p className="px-1 mb-3 text-[11px] font-black uppercase tracking-[0.22em] text-[#1a1a1a]/45" data-testid="contract-clauses-title">
          Contenu du contrat · {clauses.length} articles
        </p>
        <div className="rounded-[16px] bg-white overflow-hidden"
             style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 6px 20px -12px rgba(0,0,0,0.10), 0 0 0 0.5px rgba(0,0,0,0.05)' }}>
          {clauses.map((c, i) => (
            <div key={c.n}
                 className="flex items-start gap-3.5 px-4 py-3.5"
                 style={i < clauses.length - 1 ? { borderBottom: '0.5px solid rgba(0,0,0,0.06)' } : {}}
                 data-testid={`contract-clause-${c.n}`}>
              <div className="flex-shrink-0 flex items-center justify-center"
                   style={{
                     width: 34, height: 34, borderRadius: 10,
                     background: accent,
                     color: '#FFFFFF',
                     fontFamily: '"Playfair Display", serif',
                     fontSize: 14, fontWeight: 900,
                     boxShadow: `0 4px 10px -2px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.20)`,
                   }}>
                {c.n}
              </div>
              <div className="flex-1 min-w-0">
                <p style={{ fontSize: 14.5, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em', lineHeight: 1.25 }}>
                  Art. {c.n} — {c.t}
                </p>
                <p style={{ fontSize: 12.5, color: '#1a1a1a', opacity: 0.6, marginTop: 3, lineHeight: 1.4, letterSpacing: '-0.005em' }}>
                  {c.d}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Legal note */}
        <div className="mt-5 rounded-[14px] px-4 py-3.5"
             style={{ background: `${accent}0F`, border: `1px solid ${accent}30` }}>
          <p style={{ fontSize: 12, color: accentDark, lineHeight: 1.5, fontWeight: 600 }}>
            🔒 En signant, tu recevras le PDF complet du contrat à ton email (référence unique).
            {isEconomie
              ? ' Tu seras ensuite redirigé vers Stripe pour finaliser le paiement.'
              : isConfigurateur
                ? ' Je reviens vers toi sous 24h avec le devis nominatif détaillé à annexer.'
                : ' Je reviens vers toi sous 24h avec le devis nominatif à annexer.'}
          </p>
        </div>

        {/* Full contract preview (embedded) */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3 px-1">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#1a1a1a]/45">
              Aperçu complet du contrat
            </p>
            <button
              type="button"
              onClick={() => {
                let url = `${API_URL}/api/contract/preview?offer=${offerKey}`;
                if (isConfigurateur && configPrefill) {
                  const params = new URLSearchParams({
                    offer: 'configurateur',
                    config_format: configPrefill.format || 'distanciel',
                    config_country: configPrefill.country || '',
                    config_chantiers: (configPrefill.chantiers || []).join(','),
                    config_full_pack: String(!!configPrefill.full_pack),
                  });
                  if (configPrefill.total_ht != null) params.set('config_total_ht', String(configPrefill.total_ht));
                  url = `${API_URL}/api/contract/preview?${params.toString()}`;
                }
                window.open(url, '_blank', 'noopener');
              }}
              className="text-[10.5px] font-black uppercase tracking-[0.14em] px-2.5 py-1 rounded-full"
              style={{ background: `${accent}18`, color: accentDark, border: `0.5px solid ${accent}40` }}
              data-testid={`contract-preview-open-${offerKey}`}
            >
              Ouvrir ↗
            </button>
          </div>
          <div
            className="rounded-[16px] bg-white overflow-y-auto contract-preview-scope"
            style={{
              boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 6px 20px -12px rgba(0,0,0,0.10), 0 0 0 0.5px rgba(0,0,0,0.05)',
              maxHeight: 520,
              padding: '18px 20px',
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 11.5,
              lineHeight: 1.55,
              color: '#1a1a1a',
            }}
            data-testid={`contract-embed-${offerKey}`}
            onScroll={(e) => {
              const el = e.currentTarget;
              const pct = (el.scrollTop + el.clientHeight) / el.scrollHeight;
              if (pct >= 0.7) window.__contractPreviewRead70 = true;
            }}
          >
            {previewHtml ? (
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            ) : (
              <p className="text-center text-sm text-gray-400 py-8">Chargement de l&apos;aperçu…</p>
            )}
          </div>
          <p className="mt-2 px-1 text-[11px] text-[#5A5A5A] italic">
            💡 Défile dans l&apos;aperçu pour lire les {clauses.length} articles. Tes infos personnelles seront ajoutées automatiquement au moment de la signature.
          </p>
        </div>
      </div>

      {/* Fixed bottom CTA */}
      <div className="fixed left-0 right-0 z-30 px-4 pb-5 pt-3"
           style={{ bottom: 0, background: 'linear-gradient(0deg, #f5f3f0 55%, rgba(245,243,240,0) 100%)' }}>
        <button
          type="button"
          onClick={openModal}
          className="w-full py-4 active:scale-[0.98] transition flex items-center justify-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
            color: '#FFFFFF',
            borderRadius: 16,
            fontFamily: '"Playfair Display", serif',
            fontSize: 16, fontWeight: 900,
            letterSpacing: '-0.01em',
            boxShadow: `0 14px 32px -8px ${accent}66`,
          }}
          data-testid={`contract-page-cta-${offerKey}`}
        >
          <FileText className="w-4 h-4" />
          {isEconomie
            ? 'Signer et payer maintenant'
            : isConfigurateur
              ? 'Signer mon contrat sur-mesure'
              : 'Signer mon contrat de mission'} →
        </button>
        <p style={{ fontSize: 10.5, color: '#5A5A5A', textAlign: 'center', marginTop: 8, letterSpacing: '0.01em' }}>
          Signature électronique · Art. 1367 Code civil · RGPD
        </p>
      </div>

      {/* Mount modal */}
      <ContractSignModal />
    </div>
  );
}
